import React, { useEffect, useState } from 'react';
import { Trash2, X, AlertCircle, ClipboardCheck, Eye, Printer, User } from 'lucide-react';
import { getStokOpname, getStokOpnameById, createStokOpname, deleteStokOpname, getInventoriSpareparts, getGudang } from '../../api';
import { useAuth } from '../../AuthContext';
import SearchableSelect from '../../components/SearchableSelect';

export default function StokOpname() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [gudangList, setGudangList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [detailFull, setDetailFull] = useState(null); // hasil fetch by ID (berisi items)
  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10), gudang_id: '', keterangan: '',
    items: [{ sparepart_id: '', qty_fisik: 0, keterangan: '' }]
  });

  const load = () => {
    setLoading(true);
    Promise.all([getStokOpname(), getInventoriSpareparts({}), getGudang()])
      .then(([res, spRes, gRes]) => { setData(res.data); setSpareparts(spRes.data || []); setGudangList(gRes.data || []); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { sparepart_id: '', qty_fisik: 0, keterangan: '' }] }));
  const updItem = (idx, field, value) => {
    const items = [...form.items]; items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const delItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Stok yang ditampilkan mengikuti gudang terpilih (model stok per gudang)
  const effectiveStock = (p) => {
    const gid = Number(form.gudang_id) || 0;
    const pg = p.stok_per_gudang;
    if (pg && Object.keys(pg).length > 0) {
      if (!gid) return 0;
      return Number(pg[String(gid)]) || 0;
    }
    const home = Number(p.gudang_id) || 0;
    if (home > 0 && gid > 0 && home !== gid) return 0;
    return Number(p.stock) || 0;
  };
  const sparepartsForGudang = spareparts.map(p => ({ ...p, stock: effectiveStock(p) }));

  const handleSave = async () => {
    setError('');
    if (form.items.length === 0 || !form.items[0].sparepart_id) { setError('Minimal 1 item sparepart'); return; }
    setSaving(true);
    try {
      await createStokOpname({
        tanggal: form.tanggal, gudang_id: parseInt(form.gudang_id) || 0, keterangan: form.keterangan,
        items: form.items.filter(i => i.sparepart_id).map(i => ({
          sparepart_id: Number(i.sparepart_id), qty_fisik: Number(i.qty_fisik), keterangan: i.keterangan
        }))
      });
      setShowModal(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus opname ini?')) return;
    try { await deleteStokOpname(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  // Buka detail lengkap — fetch by ID karena list tidak memuat items
  const viewDetail = async (id) => {
    const row = data.find(d => d.id === id);
    setDetailItem(row || { id });
    setDetailFull(null);
    try {
      const res = await getStokOpnameById(id);
      setDetailFull(res.data);
    } catch (err) { console.error(err); setDetailFull({ id }); }
  };

  // ===== PDF Cetak Berita Acara Stok Opname =====
  const generateOpnamePDF = async (item) => {
    if (!item) return;
    try {
      let d = detailFull && detailFull.id === item.id ? detailFull : item;
      if (!d.items) {
        const res = await getStokOpnameById(item.id);
        d = res.data || d;
      }
      if (!d) return;

      const { jsPDF } = require('jspdf');
      const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 20;

      const primary = [79, 110, 247];
      const primaryDark = [59, 90, 227];
      const dark = [23, 43, 77];
      const gray = [136, 152, 170];
      const fmt = (v) => (Number(v) || 0).toLocaleString('id-ID');

      // === HEADER ===
      doc.setFillColor(...primary);
      doc.rect(0, 0, pageW, 44, 'F');
      doc.setFillColor(...primaryDark);
      doc.rect(0, 0, pageW, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('BERITA ACARA STOK OPNAME', margin, 20);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`No. Ref: ${d.no_ref || '-'}`, margin, 30);
      doc.text(`Tanggal: ${d.tanggal || '-'}`, margin, 37);

      // Info perusahaan kanan
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Layanan Synexa', pageW - margin, 17, { align: 'right' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Fleet Management System', pageW - margin, 23, { align: 'right' });
      doc.text('Inventory / Stok Opname', pageW - margin, 29, { align: 'right' });

      // Status badge
      const stColor = d.status === 'Selesai' ? [209, 250, 229] : [243, 244, 246];
      const stTxt = d.status === 'Selesai' ? [6, 95, 70] : [55, 65, 81];
      doc.setFillColor(...stColor);
      doc.roundedRect(pageW - margin - 42, 5, 38, 9, 2, 2, 'F');
      doc.setTextColor(...stTxt);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(d.status || '-', pageW - margin - 23, 11, { align: 'center' });

      // === INFO TRANSAKSI ===
      let y = 55;
      doc.setFillColor(...primary);
      doc.rect(margin, y, 3, 8, 'F');
      doc.setTextColor(...dark);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('INFORMASI OPNAME', margin + 7, y + 6);
      y += 14;

      const infoRows = [
        ['No. Ref', d.no_ref || '-'],
        ['Tanggal', d.tanggal || '-'],
        ['Gudang', d.gudang_nama || '-'],
        ['Dibuat Oleh', d.created_by_name || '-'],
        ['Keterangan', d.keterangan || '-'],
      ];
      doc.setFontSize(9);
      infoRows.forEach(([label, value], i) => {
        const yy = y + i * 6;
        doc.setTextColor(...gray);
        doc.setFont('helvetica', 'bold');
        doc.text(label, margin, yy);
        doc.setTextColor(...dark);
        doc.setFont('helvetica', 'normal');
        doc.text(':  ' + (value || '-'), margin + 40, yy);
      });
      y += infoRows.length * 6 + 6;

      // === TABEL ITEM ===
      const items = (d.items || []).map(it => [
        it.code || '-',
        it.name || '-',
        fmt(it.qty_sistem),
        fmt(it.qty_fisik),
        (it.selisih > 0 ? '+' : '') + fmt(it.selisih),
        it.keterangan || '',
      ]);
      autoTable(doc, {
        startY: y,
        head: [['Kode', 'Nama Sparepart', 'Qty Sistem', 'Qty Fisik', 'Selisih', 'Keterangan']],
        body: items,
        theme: 'striped',
        headStyles: { fillColor: primary, fontSize: 8.5 },
        styles: { fontSize: 8, cellPadding: 2.5 },
        columnStyles: {
          0: { cellWidth: 24 },
          2: { cellWidth: 18, halign: 'right' },
          3: { cellWidth: 18, halign: 'right' },
          4: { cellWidth: 18, halign: 'right' },
        },
        margin: { left: margin, right: margin },
      });

      // === RINGKASAN ===
      const finalY = doc.lastAutoTable.finalY + 8;
      const totalItems = (d.items || []).length;
      const totalSelisih = (d.items || []).reduce((s, it) => s + (Number(it.selisih) || 0), 0);
      const plusCount = (d.items || []).filter(it => it.selisih > 0).length;
      const minusCount = (d.items || []).filter(it => it.selisih < 0).length;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...dark);
      doc.text(
        `Total Item: ${totalItems}  |  Selisih: ${totalSelisih > 0 ? '+' : ''}${fmt(totalSelisih)}  (lebih ${plusCount}, kurang ${minusCount})`,
        margin, finalY
      );

      // === FOOTER ===
      const now = new Date().toLocaleString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...gray);
      doc.text(`Dicetak: ${now} | LayananSynexa FMS`, margin, pageH - 8);

      doc.save(`StokOpname_${d.no_ref || d.id}.pdf`);
    } catch (err) {
      console.error('Gagal cetak opname', err);
      alert('Gagal mencetak PDF opname');
    }
  };

  // Ringkasan untuk modal detail
  const detailSummary = detailFull && detailFull.items
    ? {
        total: detailFull.items.length,
        selisih: detailFull.items.reduce((s, it) => s + (Number(it.selisih) || 0), 0),
        plus: detailFull.items.filter(it => it.selisih > 0).length,
        minus: detailFull.items.filter(it => it.selisih < 0).length,
      }
    : null;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Stok Opname</h2>
            <p className="page-desc">{data.length} opname tercatat</p>
          </div>
          {hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={() => { setError(''); setShowModal(!showModal); }}>
              <ClipboardCheck size={16} /><span>{showModal ? 'Tutup Form' : 'Buat Opname'}</span>
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <div style={{
          marginTop: 16, padding: '20px 22px', borderRadius: 12,
          background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 14px rgba(15,23,42,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClipboardCheck size={18} style={{ color: 'var(--primary, #4f46e5)' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>Buat Stok Opname</h3>
            </div>
            <button className="btn-icon" onClick={() => setShowModal(false)} title="Tutup form"><X size={18} /></button>
          </div>
          {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
          <div className="form-grid">
            <div className="form-group"><label>Tanggal *</label><input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} /></div>
            <div className="form-group"><label>Gudang</label>
              <select value={form.gudang_id} onChange={e => setForm({...form, gudang_id: e.target.value})}>
                <option value="">-- Pilih Gudang --</option>
                {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
              </select>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>Stok item mengikuti gudang terpilih — item yang tidak berada di gudang ini tampil <b>stok 0</b>.</div>
            </div>
          </div>
          <div className="form-group"><label>Keterangan</label><input type="text" value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} placeholder="Contoh: Opname bulanan gudang Depok" /></div>

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: 2 }}>Item Opname</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 8 }}>Ketik nama / kode sparepart untuk mencari. Qty fisik = hasil hitung di lapangan.</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '0 2px', marginBottom: 4 }}>
              <div style={{ flex: 3, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Sparepart *</div>
              <div style={{ width: 110, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Qty Fisik *</div>
              <div style={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Keterangan</div>
              <div style={{ width: 28 }} />
            </div>
            {form.items.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                <SearchableSelect
                  options={sparepartsForGudang}
                  value={item.sparepart_id}
                  onChange={v => updItem(idx, 'sparepart_id', v)}
                  placeholder="Ketik kode / nama item..."
                />
                <input type="number" min="0" value={item.qty_fisik} onChange={e => updItem(idx, 'qty_fisik', e.target.value)} placeholder="0" style={{ width: 110, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                <input type="text" value={item.keterangan} onChange={e => updItem(idx, 'keterangan', e.target.value)} placeholder="Keterangan item" style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                <button className="btn-icon btn-danger" onClick={() => delItem(idx)} disabled={form.items.length === 1} title="Hapus item"><X size={14} /></button>
              </div>
            ))}
            <button className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 12px' }} onClick={addItem}>+ Tambah Item</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan...' : <><ClipboardCheck size={16} /> Proses Opname</>}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th><th>Tanggal</th><th>Gudang</th><th>Keterangan</th><th>Status</th><th>Dibuat Oleh</th>
                <th style={{ width: 130 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(d => (
                <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => viewDetail(d.id)}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td>{d.gudang_nama || '-'}</td>
                  <td className="text-muted">{d.keterangan || '-'}</td>
                  <td><span className={`badge ${d.status === 'Selesai' ? 'badge-success' : ''}`}>{d.status}</span></td>
                  <td><span className="cell-with-icon"><User size={12} className="text-muted" />{d.created_by_name || '-'}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="action-btns">
                      <button className="btn-icon" title="Lihat Detail" onClick={() => viewDetail(d.id)} style={{ color: '#11cdef' }}><Eye size={14} /></button>
                      <button className="btn-icon" title="Cetak PDF" onClick={() => generateOpnamePDF(d)} style={{ color: '#2dce89' }}><Printer size={14} /></button>
                      {hasMenuAction('inventori', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} className="text-center text-muted" style={{ padding: 40 }}>Belum ada stok opname</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {detailItem && (
        <div className="modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detail Opname {detailItem.no_ref}</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                  onClick={() => generateOpnamePDF(detailItem)}>
                  <Printer size={14} /> Cetak PDF
                </button>
                <button className="btn-icon" onClick={() => setDetailItem(null)}><X size={18} /></button>
              </div>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div><label>No. Ref</label><div><strong>{detailItem.no_ref}</strong></div></div>
                <div><label>Tanggal</label><div>{detailItem.tanggal}</div></div>
                <div><label>Gudang</label><div>{detailItem.gudang_nama || '-'}</div></div>
                <div><label>Status</label>
                  <div><span className={`badge ${detailItem.status === 'Selesai' ? 'badge-success' : ''}`}>{detailItem.status || '-'}</span></div>
                </div>
                <div><label>Dibuat Oleh</label><div>{detailItem.created_by_name || '-'}</div></div>
                <div style={{ gridColumn: 'span 2' }}><label>Keterangan</label><div>{detailItem.keterangan || '-'}</div></div>
              </div>

              {detailFull === null && (
                <div className="page-loading" style={{ padding: 20 }}>Memuat detail item...</div>
              )}

              {detailFull && detailFull.items && detailFull.items.length > 0 && (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr><th>Kode</th><th>Sparepart</th><th>Qty Sistem</th><th>Qty Fisik</th><th>Selisih</th><th>Keterangan</th></tr>
                    </thead>
                    <tbody>
                      {detailFull.items.map((it, i) => (
                        <tr key={i}>
                          <td><code>{it.code}</code></td>
                          <td>{it.name}</td>
                          <td>{it.qty_sistem}</td>
                          <td style={{ fontWeight: 600 }}>{it.qty_fisik}</td>
                          <td style={{ color: it.selisih > 0 ? '#2dce89' : it.selisih < 0 ? '#f5365c' : 'inherit', fontWeight: 600 }}>
                            {it.selisih > 0 ? '+' : ''}{it.selisih}
                          </td>
                          <td>{it.keterangan || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {detailSummary && (
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.8rem', color: '#475569' }}>
                      <span><strong>Total Item:</strong> {detailSummary.total}</span>
                      <span>
                        <strong>Total Selisih:</strong>{' '}
                        <span style={{ color: detailSummary.selisih > 0 ? '#2dce89' : detailSummary.selisih < 0 ? '#f5365c' : 'inherit', fontWeight: 700 }}>
                          {detailSummary.selisih > 0 ? '+' : ''}{detailSummary.selisih}
                        </span>
                      </span>
                      <span><strong>Lebih (fisik &gt; sistem):</strong> {detailSummary.plus} item</span>
                      <span><strong>Kurang (fisik &lt; sistem):</strong> {detailSummary.minus} item</span>
                    </div>
                  )}
                </div>
              )}
              {detailFull && (!detailFull.items || detailFull.items.length === 0) && (
                <div className="text-center text-muted" style={{ padding: 24 }}>Tidak ada item pada opname ini</div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
